import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Trash2, Phone, Calendar, UserCheck } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

interface UpcomingCall {
  id: string;
  connection_id: string;
  requester_id: string;
  recipient_id: string;
  status: string;
  created_at: string;
  expires_at: string;
  scheduled_at: string | null;
  requester_name: string;
  requester_avatar: string | null;
  recipient_name: string;
  recipient_avatar: string | null;
}

export function UpcomingCallsManager() {
  const queryClient = useQueryClient();
  const [callToDelete, setCallToDelete] = useState<UpcomingCall | null>(null);

  // Fetch upcoming calls (scheduling requests with scheduled_at in the future)
  const { data: upcomingCalls, isLoading } = useQuery({
    queryKey: ["admin_upcoming_calls"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scheduling_requests")
        .select(`
          id,
          connection_id,
          requester_id,
          recipient_id,
          status,
          created_at,
          expires_at,
          connection_requests!inner(scheduled_at)
        `)
        .not("connection_requests.scheduled_at", "is", null)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Get unique user IDs
      const userIds = [...new Set(data?.flatMap((d) => [d.requester_id, d.recipient_id]) || [])];

      // Fetch profiles for all users
      let profiles: { id: string; full_name: string; avatar_url: string | null }[] = [];
      if (userIds.length > 0) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("id, full_name, avatar_url")
          .in("id", userIds);
        profiles = profileData || [];
      }

      // Map data with profile info
      return data?.map((call) => {
        const requester = profiles.find((p) => p.id === call.requester_id);
        const recipient = profiles.find((p) => p.id === call.recipient_id);
        const connectionData = call.connection_requests as unknown as { scheduled_at: string | null };
        
        return {
          id: call.id,
          connection_id: call.connection_id,
          requester_id: call.requester_id,
          recipient_id: call.recipient_id,
          status: call.status,
          created_at: call.created_at,
          expires_at: call.expires_at,
          scheduled_at: connectionData?.scheduled_at || null,
          requester_name: requester?.full_name || "Unknown",
          requester_avatar: requester?.avatar_url || null,
          recipient_name: recipient?.full_name || "Unknown",
          recipient_avatar: recipient?.avatar_url || null,
        } as UpcomingCall;
      }) || [];
    },
  });

  // Delete call mutation
  const deleteCall = useMutation({
    mutationFn: async (call: UpcomingCall) => {
      // Delete the scheduling request
      const { error: schedError } = await supabase
        .from("scheduling_requests")
        .delete()
        .eq("id", call.id);

      if (schedError) throw schedError;

      // Clear the scheduled_at from the connections table
      const { error: connError } = await supabase
        .from("connections")
        .update({ scheduled_at: null, zoom_link: null })
        .eq("id", call.connection_id);

      if (connError) throw connError;

      return call;
    },
    onSuccess: (call) => {
      queryClient.invalidateQueries({ queryKey: ["admin_upcoming_calls"] });
      toast.success(`Call between ${call.requester_name} and ${call.recipient_name} has been deleted`);
      setCallToDelete(null);
    },
    onError: (error: Error) => {
      toast.error("Failed to delete call: " + error.message);
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!upcomingCalls || upcomingCalls.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Phone className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No upcoming calls scheduled</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {upcomingCalls.length} upcoming call{upcomingCalls.length !== 1 ? "s" : ""}
        </p>
      </div>

      {upcomingCalls.map((call) => (
        <Card key={call.id}>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="flex items-center">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={call.requester_avatar || undefined} />
                    <AvatarFallback>{call.requester_name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <span className="mx-2 text-muted-foreground">↔</span>
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={call.recipient_avatar || undefined} />
                    <AvatarFallback>{call.recipient_name.charAt(0)}</AvatarFallback>
                  </Avatar>
                </div>
                <div>
                  <p className="font-medium">
                    {call.requester_name} & {call.recipient_name}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {call.scheduled_at
                      ? format(new Date(call.scheduled_at), "MMM d, yyyy 'at' h:mm a")
                      : "Not scheduled yet"}
                  </div>
                </div>
              </div>

              <Badge variant={call.status === "pending" ? "secondary" : "default"}>
                {call.status}
              </Badge>

              <Button
                size="sm"
                variant="destructive"
                onClick={() => setCallToDelete(call)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      <AlertDialog open={!!callToDelete} onOpenChange={(open) => !open && setCallToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Upcoming Call</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this scheduled call between{" "}
              <strong>{callToDelete?.requester_name}</strong> and{" "}
              <strong>{callToDelete?.recipient_name}</strong>?
              {callToDelete?.scheduled_at && (
                <>
                  <br />
                  <br />
                  Scheduled for: {format(new Date(callToDelete.scheduled_at), "MMM d, yyyy 'at' h:mm a")}
                </>
              )}
              <br />
              <br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => callToDelete && deleteCall.mutate(callToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteCall.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Call
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
